import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton,
  TextField, Button, MenuItem, CircularProgress,
  Alert, Box, Typography, Slide, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import { TransitionProps } from '@mui/material/transitions';
import React from 'react';
import axios from 'axios';
import { ProjectConfig } from 'Global';
import AutocompleteField from 'elements/AutocompleteField';
import { ADMIN_WHATSAPP } from './quotesConfig';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PROCEDIMIENTOS = [
  { value: 'curacion_simple', label: 'Curación simple' },
  { value: 'curacion_avanzada', label: 'Curación avanzada de heridas' },
  { value: 'ostomia', label: 'Ostomía' },
  { value: 'sondas', label: 'Sondas' },
  { value: 'retiro_puntos', label: 'Retiro de puntos' },
  { value: 'inyecciones', label: 'Inyecciones' },
  { value: 'tratamiento_endovenoso', label: 'Tratamiento endovenoso / Sueroterapia' },
  { value: 'asesoria_online', label: 'Asesoría online' },
  { value: 'otro', label: 'Otro procedimiento' },
];

const REQUIERE_FOTOS = ['curacion_simple', 'curacion_avanzada'];

// Campos adicionales de un solo clic, específicos por tipo de procedimiento.
interface CampoEspecifico {
  key: string;
  label: string;
  type: 'buttons' | 'text';
  options?: string[];
  required?: boolean;
}

const CAMPOS_POR_PROCEDIMIENTO: Record<string, CampoEspecifico[]> = {
  inyecciones: [
    { key: 'tipo_inyeccion', label: 'Tipo', type: 'buttons', options: ['Intramuscular', 'Endovenosa', 'Subcutánea'], required: true },
    { key: 'cantidad', label: 'Cantidad', type: 'buttons', options: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10+'], required: true },
    { key: 'medicamentos_inyectar', label: 'Medicamentos a inyectar', type: 'text', required: true },
    { key: 'tiene_medicamento', label: '¿Tienes el medicamento?', type: 'buttons', options: ['Sí', 'No'], required: true },
    { key: 'tiene_orden_medica', label: '¿Tienes orden médica?', type: 'buttons', options: ['Sí', 'No'], required: true },
  ],
  ostomia: [
    { key: 'tiene_placas_materiales', label: '¿Cuentas con las placas y materiales para el cambio?', type: 'buttons', options: ['Sí', 'No'], required: true },
  ],
  sondas: [
    { key: 'tipo_sonda', label: 'Tipo', type: 'buttons', options: ['Nasogástrica', 'Foley/Urinaria'], required: true },
    { key: 'tiene_sonda_materiales', label: '¿Cuentas con la sonda y materiales?', type: 'buttons', options: ['Sí', 'No'], required: true },
  ],
  retiro_puntos: [
    { key: 'indicacion_medica', label: '¿Tienes indicación médica para el retiro?', type: 'buttons', options: ['Sí, escrita', 'Sí, verbal', 'No'], required: true },
  ],
  curacion_simple: [
    { key: 'diagnostico_herida', label: 'Diagnóstico o tipo de herida', type: 'text', required: false },
  ],
  curacion_avanzada: [
    { key: 'diagnostico_herida', label: 'Diagnóstico o tipo de herida', type: 'text', required: false },
  ],
  tratamiento_endovenoso: [
    { key: 'tiene_insumos_tratamiento', label: '¿Cuenta con los insumos para el tratamiento?', type: 'buttons', options: ['Sí', 'No'], required: true },
    { key: 'tiene_orden_medica_tratamiento', label: '¿Cuenta con orden médica?', type: 'buttons', options: ['Sí', 'No'], required: true },
  ],
};

const RANGOS_HORARIOS = [
  { value: 'manana', label: 'Mañana (08:00 - 12:00)' },
  { value: 'tarde', label: 'Tarde (12:00 - 17:00)' },
  { value: 'tarde_noche', label: 'Tarde-noche (17:00 - 21:00)' },
  { value: 'todo_dia', label: 'Todo el día (08:00 - 21:00)' },
  { value: 'personalizado', label: 'Otro horario (especificar)' },
];

interface QuoteFormProps {
  open: boolean;
  onClose: () => void;
}

export default function QuoteForm({ open, onClose }: QuoteFormProps) {
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [comunaId, setComunaId] = useState<number | null>(null);
  const [comunaLabel, setComunaLabel] = useState<string>('');
  const [camposEspecificos, setCamposEspecificos] = useState<Record<string, string>>({});
  const [rangoHorarioCustom, setRangoHorarioCustom] = useState('');

  const [form, setForm] = useState({
    nombre: '',
    apellido: '',
    direccion: '',
    telefono: '',
    email: '',
    procedimiento: '',
    fecha_deseada: '',
    rango_horario: '',
    descripcion: '',
  });

  const set = (k: string) => (e: any) =>
    setForm(prev => ({ ...prev, [k]: e.target.value }));

  const setProcedimiento = (e: any) => {
    setForm(prev => ({ ...prev, procedimiento: e.target.value }));
    setCamposEspecificos({});
  };

  const setCampoEspecifico = (key: string) => (_e: any, value: string | null) => {
    if (value === null) return; // ToggleButtonGroup exclusive: ignora el deselect
    setCamposEspecificos(prev => ({ ...prev, [key]: value }));
  };

  const today = new Date().toISOString().split('T')[0];

  const needsPhotos = REQUIERE_FOTOS.includes(form.procedimiento);
  const camposDelProcedimiento = CAMPOS_POR_PROCEDIMIENTO[form.procedimiento] || [];

  const handlePhotoAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter(f => f.type.startsWith('image/'));
    if (photos.length + valid.length > 5) {
      setError('Máximo 5 fotos'); return;
    }
    setPhotos(prev => [...prev, ...valid]);
    setPhotoPreviews(prev => [...prev, ...valid.map(f => URL.createObjectURL(f))]);
    e.target.value = '';
  };

  const removePhoto = (i: number) => {
    setPhotos(prev => prev.filter((_, idx) => idx !== i));
    setPhotoPreviews(prev => prev.filter((_, idx) => idx !== i));
  };

  const handleSubmit = async () => {
    setError('');
    const { nombre, apellido, telefono, procedimiento, fecha_deseada, rango_horario } = form;
    if (!nombre || !apellido || !telefono || !procedimiento || !comunaId || !fecha_deseada || !rango_horario) {
      setError('Por favor completa los campos obligatorios'); return;
    }
    if (rango_horario === 'personalizado' && !rangoHorarioCustom.trim()) {
      setError('Por favor especifica el horario que te acomoda'); return;
    }
    const faltaCampoEspecifico = camposDelProcedimiento.some(c => c.required && !camposEspecificos[c.key]);
    if (faltaCampoEspecifico) {
      setError('Por favor completa los campos obligatorios del procedimiento'); return;
    }
    if (needsPhotos && photos.length === 0) {
      setError('Debes subir al menos una fotografía reciente de la herida'); return;
    }

    const rangoHorarioFinal = rango_horario === 'personalizado' ? rangoHorarioCustom.trim() : rango_horario;

    setLoading(true);
    try {
      const body = new FormData();
      body.append('formValues', JSON.stringify({ ...form, rango_horario: rangoHorarioFinal, comuna_id: comunaId, campos_especificos: camposEspecificos }));
      photos.forEach((f, i) => body.append(`photo_${i}`, f, f.name));

      const resp = await axios.post(`${ProjectConfig.api_url}/quotes/submit`, body);

      if (resp.data.status === 'success') {
        // Open admin WhatsApp with a prefilled message and the review link
        const procedimientoLabel = PROCEDIMIENTOS.find(p => p.value === procedimiento)?.label || procedimiento;
        const rangoLabel = rango_horario === 'personalizado'
          ? rangoHorarioFinal
          : RANGOS_HORARIOS.find(r => r.value === rango_horario)?.label || rango_horario;
        const fechaFmt = new Date(fecha_deseada + 'T00:00:00').toLocaleDateString('es-CL');
        const link = `${window.location.origin}/admin/cotizaciones/${resp.data.request_id}`;
        const msg = encodeURIComponent(
          `Hola especialista, soy ${nombre} ${apellido}, necesito ${procedimientoLabel} en la comuna de ${comunaLabel}, para el día ${fechaFmt} en el horario ${rangoLabel}. Ver formulario: ${link}\n\nRecuerda enviar este mensaje para que nuestro equipo gestione tu solicitud.`
        );
        window.open(`https://wa.me/${ADMIN_WHATSAPP}?text=${msg}`, '_blank');
        setStep('success');
      } else {
        setError(resp.data.message || 'Error al enviar el formulario');
      }
    } catch (e: any) {
      setError('Error de conexión. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('form');
    setError('');
    setPhotos([]);
    setPhotoPreviews([]);
    setComunaId(null);
    setComunaLabel('');
    setCamposEspecificos({});
    setRangoHorarioCustom('');
    setForm({ nombre: '', apellido: '', direccion: '', telefono: '', email: '', procedimiento: '', fecha_deseada: '', rango_horario: '', descripcion: '' });
    onClose();
  };

  const sectionSx = {
    display: 'flex', flexDirection: 'column', gap: 2,
    bgcolor: '#fff', border: '1px solid #e3ecf7', borderRadius: 3,
    p: 2.5,
  };

  const sectionHeaderSx = {
    display: 'flex', alignItems: 'center', gap: 1,
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #0C3A6E 0%, #125095 55%, #1a6ac8 100%)',
          py: 2.5,
        }}
      >
        <Box>
          <Typography fontWeight={700} fontSize={19} color="#fff">
            Solicitar cotización de enfermeras
          </Typography>
          <Typography fontSize={13} color="rgba(255,255,255,0.8)" sx={{ mt: .3 }}>
            Completa una vez y compara respuestas de varias profesionales
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: '#f4f8fc', p: { xs: 2, sm: 3 } }}>
        {step === 'success' ? (
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 72, color: '#1f7a52', mb: 2 }} />
            <Typography variant="h6" fontWeight={700} gutterBottom>
              ¡Formulario enviado!
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Tu formulario fue enviado exitosamente. En un máximo de <strong>45 minutos hábiles</strong> recibirás cotizaciones de las enfermeras disponibles en tu comuna.
            </Typography>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                background: 'linear-gradient(135deg, #125095 0%, #1a6ac8 100%)',
                borderRadius: 2, px: 4, fontWeight: 700,
              }}
            >
              Entendido
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

            <Box sx={sectionSx}>
              <Box sx={sectionHeaderSx}>
                <PersonOutlineIcon sx={{ color: '#4B9FDB' }} />
                <Typography fontWeight={700} fontSize={15} color="#125095">
                  Datos personales
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField label="Nombre *" size="small" value={form.nombre} onChange={set('nombre')} />
                <TextField label="Apellido *" size="small" value={form.apellido} onChange={set('apellido')} />
              </Box>

              <TextField label="Teléfono *" size="small" placeholder="+569..." value={form.telefono} onChange={set('telefono')} />

              <TextField label="Correo electrónico" size="small" type="email" value={form.email} onChange={set('email')} />

              <AutocompleteField
                label="Comuna donde necesita el servicio *"
                endpoint="/locations/comunas/list"
                onChange={(val: any) => { setComunaId(val?.value ?? null); setComunaLabel(val?.label ?? ''); }}
                value={comunaId}
              />

              <TextField
                label="Dirección de referencia o sector"
                size="small"
                value={form.direccion}
                onChange={set('direccion')}
                placeholder="Ej: Cerca del hospital, Villa Los Aromos, etc."
              />
            </Box>

            <Box sx={sectionSx}>
              <Box sx={sectionHeaderSx}>
                <MedicalServicesOutlinedIcon sx={{ color: '#4B9FDB' }} />
                <Typography fontWeight={700} fontSize={15} color="#125095">
                  Procedimiento
                </Typography>
              </Box>

              <TextField
                select
                label="Tipo de procedimiento *"
                size="small"
                value={form.procedimiento}
                onChange={setProcedimiento}
              >
                {PROCEDIMIENTOS.map(p => (
                  <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                ))}
              </TextField>

              {camposDelProcedimiento.map(campo => (
                <Box key={campo.key}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: .5 }}>
                    {campo.label}{campo.required ? ' *' : ''}
                  </Typography>
                  {campo.type === 'buttons' ? (
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={camposEspecificos[campo.key] || null}
                      onChange={setCampoEspecifico(campo.key)}
                      sx={{ flexWrap: 'wrap', gap: 1, '& .MuiToggleButtonGroup-grouped': { border: '1px solid #dde6f5 !important', borderRadius: '8px !important', mx: 0 } }}
                    >
                      {campo.options!.map(opt => (
                        <ToggleButton
                          key={opt}
                          value={opt}
                          sx={{
                            textTransform: 'none', fontSize: 13, px: 2, color: '#125095',
                            '&.Mui-selected': { bgcolor: '#125095', color: '#fff', '&:hover': { bgcolor: '#0e3d78' } },
                          }}
                        >
                          {opt}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      value={camposEspecificos[campo.key] || ''}
                      onChange={e => setCamposEspecificos(prev => ({ ...prev, [campo.key]: e.target.value }))}
                      placeholder={campo.required ? 'Obligatorio' : 'Opcional'}
                    />
                  )}
                </Box>
              ))}

              <TextField
                label="Fecha en que necesita el procedimiento *"
                type="date"
                size="small"
                value={form.fecha_deseada}
                onChange={set('fecha_deseada')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: today }}
              />

              <TextField
                select
                label="Horario en que puede recibir el procedimiento *"
                size="small"
                value={form.rango_horario}
                onChange={set('rango_horario')}
              >
                {RANGOS_HORARIOS.map(r => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </TextField>

              {form.rango_horario === 'personalizado' && (
                <TextField
                  label="Especifica el horario que te acomoda *"
                  size="small"
                  value={rangoHorarioCustom}
                  onChange={e => setRangoHorarioCustom(e.target.value)}
                  placeholder="Ej: entre las 15:00 y las 16:30"
                />
              )}

              <TextField
                label="Descripción adicional"
                size="small"
                multiline
                rows={3}
                value={form.descripcion}
                onChange={set('descripcion')}
                placeholder="Describe brevemente tu situación o lo que necesitas…"
              />

              {needsPhotos && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: .5, display: 'block', mb: 1 }}>
                    Fotos de la herida (obligatorio, máx. 5)
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#0C3A6E' }}>
                    Subir fotos es clave para que la enfermera pueda evaluar correctamente la herida y darte un diagnóstico y una cotización más precisos. Deben ser fotos <b>recientes</b>, tomadas con buena luz y de cerca.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {photoPreviews.map((src, i) => (
                      <Box key={i} sx={{ position: 'relative', width: 80, height: 80 }}>
                        <img
                          src={src}
                          alt={`foto-${i}`}
                          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removePhoto(i)}
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff', border: '1px solid #ddd', p: '2px' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    {photos.length < 5 && (
                      <label>
                        <input type="file" accept="image/*" multiple hidden onChange={handlePhotoAdd} />
                        <Box
                          component="span"
                          sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 80, height: 80, borderRadius: 2,
                            border: '2px dashed #bbb', cursor: 'pointer',
                            '&:hover': { borderColor: '#125095', bgcolor: '#f0f5ff' },
                          }}
                        >
                          <PhotoCameraIcon sx={{ color: '#999' }} />
                        </Box>
                      </label>
                    )}
                  </Box>
                </Box>
              )}
            </Box>

            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                background: 'linear-gradient(135deg, #125095 0%, #1a6ac8 100%)',
                borderRadius: 2, fontWeight: 700, fontSize: 15, py: 1.3,
                boxShadow: '0 4px 16px rgba(18, 80, 149, 0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #0e3d78 0%, #155ba8 100%)' },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Enviar formulario'}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
